<script setup>
import { onBeforeMount, ref, computed } from "vue";

const props = defineProps({
  cart_item: {},
  index: 0,
});

const price = ref();
const qtty = ref();
const ppId = ref();
const partial = ref(0);
const available = ref();
const index = ref();

const emit = defineEmits(["itemQttyChange"]);

onBeforeMount(() => {
  qtty.value = parseInt(props.cart_item.qtty);
  partial.value = number_format(qtty.value * props.cart_item.price);
  price.value = number_format(props.cart_item.price * 1);

  ppId.value = props.cart_item.ppId;

  available.value = parseInt(props.cart_item.available ?? -1);
  index.value = parseInt(props.index);
});

function number_format(number) {
  return Number(number).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function flattenVariaciones(variaciones) {
  const out = [];
  for (const group of variaciones || []) {
    if (Array.isArray(group)) out.push(...group);
    else if (group) out.push(group);
  }
  return out;
}

function normalizeColor(val) {
  return String(val || "").trim().replace(/^#/, "").toLowerCase();
}

function isColorValue(val) {
  if (typeof val !== "string") return false;
  const trimmed = val.trim();
  return /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed) || /^rgba?\([\d\s,.]+\)$/.test(trimmed);
}

function resolveColorStyle(val) {
  const trimmed = String(val || "").trim();
  if (/^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)) {
    return { backgroundColor: "#" + trimmed };
  }
  return { backgroundColor: trimmed };
}

function lookupColorDescripcion(hex) {
  const target = normalizeColor(hex);
  const itemPpId = props.cart_item.ppId;
  for (const v of flattenVariaciones(props.cart_item.item?.variaciones)) {
    if (!v?.atributo || !String(v.atributo).toUpperCase().includes("COLOR")) continue;
    if (itemPpId != null && v.ppId != null && Number(v.ppId) !== Number(itemPpId) && normalizeColor(v.valor) !== target) {
      continue;
    }
    if (normalizeColor(v.valor) === target && v.descripcion) return v.descripcion;
  }
  return "";
}

const variationDisplay = computed(() => {
  const presentacion = props.cart_item.Presentacion || "";
  const pName = presentacion.replace(/\s*\((.*)\)\s*$/, "").trim() || presentacion;
  const inner = presentacion.match(/\((.*)\)\s*$/)?.[1] || "";
  const parts = inner.split(" - ").map((s) => s.trim()).filter(Boolean);

  const hexPart = parts.find((p) => isColorValue(p)) || null;
  let colorDesc = hexPart ? lookupColorDescripcion(hexPart) : "";
  if (hexPart && !colorDesc) {
    const idx = parts.indexOf(hexPart);
    if (idx >= 0 && parts[idx + 1] && !isColorValue(parts[idx + 1])) {
      colorDesc = parts[idx + 1];
    }
  }

  const consumed = new Set([hexPart, colorDesc].filter(Boolean));
  const others = parts.filter((p) => !consumed.has(p));

  if (others.length === 0) {
    const itemPpId = props.cart_item.ppId;
    for (const v of flattenVariaciones(props.cart_item.item?.variaciones)) {
      if (!v?.valor || !v?.atributo) continue;
      if (String(v.atributo).toUpperCase().includes("COLOR")) continue;
      if (itemPpId != null && v.ppId != null && Number(v.ppId) !== Number(itemPpId)) continue;
      if (!consumed.has(v.valor) && !isColorValue(v.valor) && !others.includes(v.valor)) {
        others.push(v.valor);
      }
    }
  }

  return {
    pName,
    color: hexPart ? { valor: hexPart, descripcion: colorDesc } : null,
    others,
  };
});
</script>

<template>
  <div class="row stripped my-3 py-2 mx-2 py-md-0 my-md-0">
    <div class="col-12 pb-1">
      <div class="row px-3">
        <div class="col-12 col-md-4">
          {{ cart_item.item.name }}
        </div>
        <div class="col-12 col-md-2">
          <label class="text-danger presentation pl-3 flex items-center gap-1.5 flex-wrap">
            <span>{{ variationDisplay.pName }}</span>
            <span v-if="variationDisplay.color" class="inline-flex items-center gap-1">
              <span
                class="inline-block w-3.5 h-3.5 rounded-full border border-black/10 shrink-0"
                :style="resolveColorStyle(variationDisplay.color.valor)"
              ></span>
              <span v-if="variationDisplay.color.descripcion">{{ variationDisplay.color.descripcion }}</span>
            </span>
            <span v-for="(attr, i) in variationDisplay.others" :key="`${attr}-${i}`">{{ attr }}</span>
          </label>
        </div>
        <div class="d-none col-1 d-md-block category">
          {{ cart_item.item.category }}
        </div>
        <div class="d-none col-12 col-md-1 d-md-block text-end">{{ qtty }}</div>

        <div class="row col-12 d-inline d-md-none text-end">
          <div class="d-inline text-end">Cantidad:</div>
          <div class="d-inline text-end">
            {{ qtty }}
          </div>
        </div>

        <div class="d-none d-md-block col-md-2 text-end price">
          <label v-if="props.cart_item.item.show_promo">
            <del>${{ number_format(props.cart_item.originalPrice) }} </del
            ><br />
            <mark>(- {{ number_format(props.cart_item.priceDiscount) }}%)</mark
            ><br />
            ${{ number_format(props.cart_item.price) }}
          </label>
          <label v-else>${{ price }}</label>
        </div>

        <div class="row col-12 d-inline d-md-none text-end">
          <div class="text-end price">Precio unitario:</div>
          <div class="text-end price">
            <label v-if="props.cart_item.item.show_promo">
              <del>${{ number_format(props.cart_item.originalPrice) }} </del
              ><br />
              <mark
                >(- {{ number_format(props.cart_item.priceDiscount) }}%)</mark
              ><br />
              ${{ number_format(props.cart_item.price) }}
            </label>
            <label v-else>${{ price }}</label>
          </div>
        </div>

        <div class="d-none d-md-block col-md-2 text-end partial">
          ${{ partial }}
        </div>

        <div class="row col-12 d-inline d-md-none text-end">
          <div class="text-end partial">Total parcial:</div>
          <div class="text-end partial">${{ partial }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.name {
  color: #334756;
  font-weight: bold;
  font-size: 14px;
}
.category {
  color: #334756;
  text-transform: uppercase;
  font-size: 14px;
}
.price {
  color: #334756;
  font-weight: bold;
  font-size: 16px;
}
.presentation {
  color: red;
  font-size: 12px;
}
.partial {
  color: deepskyblue;
  font-weight: bold;
  font-size: 18px;
}

.stripped {
  border-radius: 15px;
}

.stripped:nth-child(odd) {
  background: ghostwhite;
  border-radius: 15px;
}
</style>
